import { useEffect, useRef, useState } from 'react';

/**
 * useScrollReveal — IntersectionObserver-based scroll reveal hook.
 *
 * Port du toolkit Intralys (Gatineau) — fires once, threshold 0.15, rootMargin -60px bottom.
 * Retourne { ref, visible }. Visible bascule à true au premier intersect puis observer disconnect.
 *
 * Usage :
 *   const { ref, visible } = useScrollReveal<HTMLDivElement>();
 *   return <div ref={ref} className={visible ? 'opacity-100' : 'opacity-0'}>...</div>;
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(threshold = 0.15) {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      // SSR / older browsers fallback : afficher immédiatement (queueMicrotask
      // pour respecter la règle no-set-state-directly-in-effect).
      queueMicrotask(() => setVisible(true));
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -60px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return { ref, visible };
}
